import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    useMediaQuery,
    Theme,
} from '@mui/material';
import { useMutation } from '@apollo/client/react/hooks';
import InfiniteScroll from 'react-infinite-scroll-component';
import TransportOrderListItem from './transport-order-item/transport-order-item.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import { ApolloCache, Reference } from '@apollo/client/core';
import TransportOrderDetailsDialog from 'components/transport-admin/transport-order-details-dialog/transport-order-details-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { TransportOrder } from 'models/transport';
import {
    updateTransportHasProblemTemplate,
    updateTransportTemplate,
} from 'apollo/templates/transport/mutation.graphql';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type TransportOrderListProps = {
    transportOrders: TransportOrder[];
    onFetchMore: () => void;
    resolveTransportProblemFlag: (transportId: string) => void;
    onUpdateTransport: (transport: TransportOrder) => void;
};
const TransportOrderList: FC<TransportOrderListProps> = ({
    transportOrders,
    onFetchMore,
    resolveTransportProblemFlag,
    onUpdateTransport,
}: TransportOrderListProps) => {
    const { t: translate } = useTranslation(['transport', 'common']);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [problemTransportId, setProblemTransportId] = useState('');
    const [currentTransport, setCurrentTransport] = useState<TransportOrder>();
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    const { showBoundary } = useDenthubErrorBoundary();

    const [resolveTransportProblem, { loading, error }] = useMutation(updateTransportHasProblemTemplate, {
        onError: (err) => {
            console.log(err, 'error');
        },
        onCompleted: () => {
            // Resolve transport order problem
            resolveTransportProblemFlag(problemTransportId);
        },
        update(cache: ApolloCache<TransportOrder[]>) {
            cache.modify({
                fields: {
                    transports(existingTransportOrderRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingTransportOrderRefs instanceof Array) {
                            return [...existingTransportOrderRefs].filter(
                                (transportOrderRef: Reference | undefined) => {
                                    return problemTransportId !== readField('_id', transportOrderRef);
                                },
                            );
                        }
                        return [];
                    },
                },
            });
        },
    });

    const [updateTransport] = useMutation(updateTransportTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ updateTransport }) => {
            onUpdateTransport(updateTransport);
        },
    });

    const handleResolveProblemClick = (transportId: string) => {
        setProblemTransportId(transportId);
        setConfirmationOpen(true);
    };

    const handleConfirmationClose = () => {
        setConfirmationOpen(false);
    };

    const handleConfirmationOk = async () => {
        await resolveTransportProblem({
            variables: { _id: problemTransportId },
        });
        setConfirmationOpen(false);
    };

    const handleDetailsClose = () => {
        setDetailsOpen(false);
    };

    const handleDetailsOk = async (transport: TransportOrder) => {
        await updateTransport({
            variables: {
                _id: transport._id,
                manualPackageCount: transport.manualPackageCount,
                comments: transport.comment,
            },
        });
        setDetailsOpen(false);
    };

    const handleListItemClick = (transport: TransportOrder) => {
        setCurrentTransport(transport);
        setDetailsOpen(true);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, transportOrders?.length || 0);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={transportOrders ? transportOrders.length : 0}
                    next={onFetchMore}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    <Table size={!isLargeScreen ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell>{translate('transport:OPTER_ID')}</TableCell>
                                <TableCell>{translate('transport:LAB')}</TableCell>
                                <TableCell>{translate('transport:CLINIC')}</TableCell>
                                <TableCell>{translate('transport:PACKAGE')}</TableCell>
                                <TableCell>{translate('transport:COMMENT')}</TableCell>
                                <TableCell>{translate('common:STATUS')}</TableCell>
                                <TableCell>{translate('common:CREATED_DATE')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Fragment>
                                {transportOrders.map((transport, index) => (
                                    <TransportOrderListItem
                                        key={index}
                                        transport={transport}
                                        onFlagClick={handleResolveProblemClick}
                                        onListItemClick={handleListItemClick}
                                    />
                                ))}
                            </Fragment>
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            {error ? <AlertSnackbar message={error.message} type="error" /> : undefined}
            {loading ? <BackdropLoader isOpen={loading} /> : undefined}
            <ConfirmationDialog
                open={confirmationOpen}
                handleClose={handleConfirmationClose}
                handleOk={handleConfirmationOk}
                title={translate('transport:RESOLVE_TITLE')}
                confirm={translate('transport:CONFIRM_RESOLVE')}
                process={translate('transport:RESOLVE')}
            />
            <TransportOrderDetailsDialog
                open={detailsOpen}
                transport={currentTransport}
                handleClose={handleDetailsClose}
                handleOk={handleDetailsOk}
            />
        </Fragment>
    );
};

export default TransportOrderList;
